import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { hasDashboardAccess, mapPlanIdToSubscriptionType } from '../../utils/accountHelpers';

export default function DashboardAccessSection({ subscription }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dashboardsText = t('account.dashboards', { returnObjects: true, defaultValue: {} }) || {};
  // Force "Dashboards disponibles" in Spanish, "Available Dashboards" in English
  const headingText = i18n.language?.startsWith('es') ? 'Dashboards disponibles' : 'Available Dashboards';

  // CRITICAL: Validate subscription to prevent rendering raw Stripe objects
  // Determine subscription type from planId (similar to SubscriptionGate)
  // The subscription object from Firebase has planId, not type
  
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardAccessSection.jsx:17',message:'Entry: Raw subscription object',data:{subscription,hasSubscription:!!subscription,subscriptionKeys:subscription?Object.keys(subscription):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
  // #endregion
  console.log('[DashboardAccessSection] Raw subscription object:', subscription);
  
  // Get status first - if expired, deny access regardless of planId
  const subscriptionStatus = subscription?.status || 'expired';
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardAccessSection.jsx:21',message:'After status extraction',data:{subscriptionStatus,rawStatus:subscription?.status,defaultedToExpired:!subscription?.status},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
  // #endregion
  
  // Determine subscription type from planId (similar to SubscriptionGate)
  const subscriptionType = subscription?.planId 
    ? mapPlanIdToSubscriptionType(subscription.planId)
    : (subscription?.type || 'expired');
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardAccessSection.jsx:26',message:'After subscription type mapping',data:{subscriptionType,planId:subscription?.planId,type:subscription?.type,mappedFromPlanId:!!subscription?.planId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
  // #endregion
  
  // Only free trials use local expiration checks.
  const now = new Date();
  const expirationDateField = subscriptionType === 'free_trial'
    ? (subscription?.trialEndsAt || subscription?.currentPeriodEnd || subscription?.expiresAt)
    : null;
  
  let isPeriodExpired = false;
  
  // For free trials, also check if sessions are exhausted
  const isSessionsExhausted = subscriptionType === 'free_trial' && 
    subscription?.sessionsRemaining !== undefined && 
    subscription.sessionsRemaining <= 0;
  
  console.log('[DashboardAccessSection] Checking period expiration:', {
    expirationDateField,
    trialEndsAt: subscription?.trialEndsAt,
    currentPeriodEnd: subscription?.currentPeriodEnd,
    expiresAt: subscription?.expiresAt,
    subscriptionKeys: subscription ? Object.keys(subscription) : [],
    subscriptionStatus,
    subscriptionType,
    sessionsRemaining: subscription?.sessionsRemaining,
    isSessionsExhausted
  });
  
  if (expirationDateField) {
    const expirationDate = new Date(expirationDateField);
    isPeriodExpired = expirationDate < now;
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardAccessSection.jsx:58',message:'Period expiration check result',data:{expirationDateField,expirationDate:expirationDate.toISOString(),now:now.toISOString(),isPeriodExpired,timeDiff:now.getTime()-expirationDate.getTime(),subscriptionStatus,subscriptionType},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    console.log('[DashboardAccessSection] Period expiration check:', {
      expirationDateField,
      expirationDate: expirationDate.toISOString(),
      now: now.toISOString(),
      isPeriodExpired,
      timeDiff: now.getTime() - expirationDate.getTime(),
      subscriptionStatus
    });
  } else {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardAccessSection.jsx:67',message:'No expiration date found',data:{hasTrialEndsAt:!!subscription?.trialEndsAt,hasCurrentPeriodEnd:!!subscription?.currentPeriodEnd,hasExpiresAt:!!subscription?.expiresAt,allKeys:subscription?Object.keys(subscription):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
    console.warn('[DashboardAccessSection] No expiration date found in subscription:', {
      subscription,
      hasTrialEndsAt: !!subscription?.trialEndsAt,
      hasCurrentPeriodEnd: !!subscription?.currentPeriodEnd,
      hasExpiresAt: !!subscription?.expiresAt,
      allKeys: subscription ? Object.keys(subscription) : []
    });
  }
  
  // For recurring Stripe plans, trust backend/Stripe status instead of local period-end math.
  const isExpired = subscriptionStatus === 'expired' || isSessionsExhausted;
  
  const finalSubscriptionType = isExpired ? 'expired' : subscriptionType;
  const finalStatus = isExpired ? 'expired' : subscriptionStatus;
  
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardAccessSection.jsx:105',message:'Expiration check result',data:{isExpired,subscriptionStatus,isPeriodExpired,isSessionsExhausted,originalType:subscriptionType,finalSubscriptionType,originalStatus:subscriptionStatus,finalStatus,reason:subscriptionStatus==='expired'?'status':isPeriodExpired?'period':'sessions'},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
  // #endregion
  if (isExpired) {
    console.log('[DashboardAccessSection] Subscription expired - forcing status/type to expired:', {
      originalStatus: subscriptionStatus,
      originalType: subscriptionType,
      finalStatus,
      finalSubscriptionType,
      reason: subscriptionStatus === 'expired' ? 'status' : isPeriodExpired ? 'period' : 'sessions'
    });
  }
  
  // Safety check: if subscription is a raw Stripe object, use 'expired' as default
  // Build full subscription object with all fields needed for access checking
  const safeSubscription = {
    type: typeof finalSubscriptionType === 'string' ? finalSubscriptionType : 'expired',
    status: finalStatus,
    planId: subscription?.planId,
    currentPeriodEnd: subscription?.currentPeriodEnd,
    expiresAt: subscription?.expiresAt || subscription?.currentPeriodEnd,
    trialEndsAt: subscription?.trialEndsAt, // Include trialEndsAt for free trials
    sessionsRemaining: subscription?.sessionsRemaining,
    cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd
  };
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardAccessSection.jsx:102',message:'Built safeSubscription object',data:{safeSubscription},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
  // #endregion

  const dashboards = [
    {
      id: 'global_markets',
      title: dashboardsText.globalMarkets || 'Global Markets Dashboard',
      description: 'S&P 500 index ETF (SPY or VOO) as primary asset',
      icon: (
        <img
          src="/images/dashboards/globalmarketsDash.svg"
          alt="Global Markets dashboard icon"
          className="w-20 h-16 object-contain"
        />
      ),
      requiredSubscription: null // Available on all tiers
    },
    {
      id: 'global_technology',
      title: dashboardsText.globalTechnology || 'Global Technology Dashboard',
      description: 'Nasdaq 100 index ETF (QQQ) as main asset',
      icon: (
        <img
          src="/images/dashboards/globalmarketsDash-1.svg"
          alt="Global Technology dashboard icon"
          className="w-20 h-16 object-contain"
        />
      ),
      requiredSubscription: 'monthly_all' // Requires Monthly All Dashboards
    },
    {
      id: 'hybrid',
      title: dashboardsText.hybrid || 'Hybrid Dashboard',
      description: 'Combines S&P 500 ETF with leveraged Nasdaq 100',
      icon: (
        <img
          src="/images/dashboards/globalmarketsDash-2.svg"
          alt="Hybrid dashboard icon"
          className="w-20 h-16 object-contain"
        />
      ),
      requiredSubscription: 'monthly_all' // Requires Monthly All Dashboards
    }
  ];

  const getSubscriptionTierForAccess = (dashboardId) => {
    if (dashboardId === 'global_markets') return null; // Available on all
    if (dashboardId === 'global_technology' || dashboardId === 'hybrid') {
      return i18n.language?.startsWith('es') ? 'Mensual Todos los Dashboards' : 'Monthly All Dashboards';
    }
    return null;
  };

  const getDashboardRoute = (dashboardId) => {
    switch (dashboardId) {
      case 'global_markets':
        return '/dashboard/global-markets';
      case 'global_technology':
        return '/dashboard/global-technology';
      case 'hybrid':
        return '/dashboard/hybrid';
      default:
        return '/dashboard';
    }
  };

  const handleDashboardClick = (dashboardId) => {
    const route = getDashboardRoute(dashboardId);
    navigate(route);
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-300 mb-6">
        {headingText}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {dashboards.map((dashboard) => {
          // Debug logging
          console.log('[DashboardAccessSection] Checking access:', {
            dashboardId: dashboard.id,
            safeSubscription,
            status: safeSubscription.status,
            type: safeSubscription.type,
            planId: safeSubscription.planId,
            currentPeriodEnd: safeSubscription.currentPeriodEnd
          });
          
          const hasAccess = hasDashboardAccess(safeSubscription, dashboard.id);
          const isLocked = !hasAccess;
          
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardAccessSection.jsx:193',message:'hasDashboardAccess result',data:{dashboardId:dashboard.id,hasAccess,isLocked,safeSubscription},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
          // #endregion
          console.log('[DashboardAccessSection] Access result:', {
            dashboardId: dashboard.id,
            hasAccess,
            isLocked
          });

          return (
            <div
              key={dashboard.id}
              className={`relative p-6 rounded-lg border transition-all ${
                isLocked
                  ? 'bg-gray-800/50 border-gray-700 opacity-60 cursor-not-allowed'
                  : 'bg-gray-800 border-gray-700 hover:border-blue-500 hover:shadow-lg cursor-pointer'
              }`}
              onClick={() => !isLocked && handleDashboardClick(dashboard.id)}
            >
              {isLocked && (
                <div className="absolute top-4 right-4">
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              )}

              <div className="mb-4">{dashboard.icon}</div>

              <h3 className="text-lg font-semibold text-white mb-2">{dashboard.title}</h3>

              <p className="text-sm text-gray-400 mb-4">{dashboard.description}</p>

              {isLocked ? (
                <button
                  disabled
                  className="w-full px-4 py-2 bg-gray-700 text-gray-400 font-medium rounded-lg cursor-not-allowed"
                  onClick={(e) => e.stopPropagation()}
                >
                  {dashboardsText.upgradeToAccess || 'Upgrade to Access'}
                </button>
              ) : (
                <button
                  className="w-full px-4 py-2 text-white font-medium rounded-lg transition-colors"
                  style={{
                    backgroundColor: dashboard.id === 'global_technology' ? '#a449ff' : dashboard.id === 'hybrid' ? '#d2aa3a' : '#3b82f6',
                    ...(dashboard.id === 'global_technology' && {
                      '--tw-shadow': '0 4px 6px -1px rgba(164, 73, 255, 0.3)',
                    }),
                  }}
                  onMouseEnter={(e) => {
                    if (dashboard.id === 'global_technology') {
                      e.currentTarget.style.backgroundColor = '#9333ea';
                    } else if (dashboard.id === 'hybrid') {
                      e.currentTarget.style.backgroundColor = '#b8912e';
                    } else {
                      e.currentTarget.style.backgroundColor = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (dashboard.id === 'global_technology') {
                      e.currentTarget.style.backgroundColor = '#a449ff';
                    } else if (dashboard.id === 'hybrid') {
                      e.currentTarget.style.backgroundColor = '#d2aa3a';
                    } else {
                      e.currentTarget.style.backgroundColor = '#3b82f6';
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDashboardClick(dashboard.id);
                  }}
                >
                  {dashboardsText.openDashboard || 'Open Dashboard'}
                </button>
              )}

              {isLocked && (
                <p className="mt-3 text-xs text-gray-500 text-center">
                  Requires: {getSubscriptionTierForAccess(dashboard.id)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

